import { Box, Button, Dialog, Paper, Theme, Toolbar, Tooltip, Typography, alpha, IconButton } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridColDef, GridRenderCellParams, GridRowModel, GridSortModel, DataGridProProps } from '@mui/x-data-grid-pro';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { DataList } from '../../components/DataList';
import UserDeleteForm from '../../components/UserDeleteForm';
import UserForm from '../../components/UserForm';
import { Maybe, MutationStatusCode, Person, AppRole } from '../../gql-types.generated';
import { selectNetworkBusy, selectViewerUser } from '../app/AppSlice';
import { fetchApteanAdminUsers, upsertApteanAdminData, deleteApteanAdminData } from './ApteanAdminUsersActions';
import {
  clearError,
  selectApteanAdminUsers,
  selectApteanAdminUsersError,
  selectUpsertPersonStatus,
  selectDeletePersonStatus,
  captureUpsertPersonStatus,
  captureDeletePersonStatus,
} from './ApteanAdminUsersSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      flex: 1,
      display: 'flex',
      flexFlow: 'column nowrap',
      height: '100%',
      minHeight: 350,
    },
    usersTable: {
      flexGrow: 1,
      display: 'flex',
    },
    toolbar: {
      borderBottomWidth: 1,
      borderBottomColor: alpha(theme.palette.common.black, 0.15),
      borderBottomStyle: 'solid',
      padding: theme.spacing(1.5, 2.5),
    },
    toolbarButton: {
      minWidth: 140,
      marginLeft: theme.spacing(1),
    },
    title: {
      flex: '1 1 100%',
    },
    tooltip: {
      fontSize: '10pt',
      textAlign: 'center',
      verticalAlign: 'middle',
    },
  }),
);

const ApteanAdminUsers: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const rows = useSelector(selectApteanAdminUsers);
  const userError = useSelector(selectApteanAdminUsersError);
  const networkBusy = useSelector(selectNetworkBusy);
  const viewerUser = useSelector(selectViewerUser);
  const upsertPersonStatus = useSelector(selectUpsertPersonStatus);
  const deletePersonStatus = useSelector(selectDeletePersonStatus);
  const [user, setUser] = useState<Person | null>();
  const [openModify, setOpenModify] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);

  useEffect(() => {
    dispatch(fetchApteanAdminUsers());
  }, []);

  useEffect(() => {
    if (upsertPersonStatus?.code === MutationStatusCode.Success) {
      dispatch(fetchApteanAdminUsers());
      dispatch(captureUpsertPersonStatus());
    }
    if (deletePersonStatus?.code === MutationStatusCode.Success) {
      dispatch(fetchApteanAdminUsers());
      dispatch(captureDeletePersonStatus());
    }
  }, [upsertPersonStatus?.code, deletePersonStatus?.code]);

  const canDeleteUser = (row: Person) => !!viewerUser?.id && viewerUser.id !== row.id;

  const onAddUserClick = () => {
    setUser({} as Person);
    dispatch(clearError());
    setOpenModify(true);
  };

  const onEditUserClick = (row: Person) => {
    setUser(row);
    dispatch(clearError());
    setOpenModify(true);
  };

  const onDeleteUserClick = (row: Person) => {
    if (canDeleteUser(row)) {
      setUser(row);
      dispatch(clearError());
      setOpenDelete(true);
    }
  };

  const submitUpsertForm = (firstName: string, lastName: string, email: string, _role: AppRole, id?: string) => {
    dispatch(clearError());
    dispatch(upsertApteanAdminData(firstName, lastName, email, id));
  };

  const submitDeleteForm = (id: string | undefined) => {
    dispatch(clearError());
    if (id) dispatch(deleteApteanAdminData(id));
  };

  const handleUserFormClose = () => {
    setOpenModify(false);
    dispatch(clearError());
    dispatch(fetchApteanAdminUsers());
  };

  const handleUserDeleteFormClose = () => {
    setOpenDelete(false);
    dispatch(clearError());
  };

  const getUserRows = () =>
    rows?.map((row: Maybe<Person>) => {
      if (!row) return {} as GridRowModel;
      return {
        _raw: row,
        id: row.id,
        name: `${row.firstName} ${row.lastName}`,
        email: row.email,
      } as GridRowModel;
    }) as GridRowModel[];

  const columns: GridColDef[] = [
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'name',
      sortable: true,
      headerName: 'Name',
      flex: 0.4,
      minWidth: 130,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'email',
      sortable: true,
      headerName: 'Email',
      flex: 0.45,
      minWidth: 130,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      sortable: false,
      field: 'action',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      flex: 0.15,
      minWidth: 120,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => {
        // eslint-disable-next-line no-underscore-dangle
        const row = params.row._raw as Person;
        return (
          <>
            <Tooltip title={<span className={classes.tooltip}>Edit</span>} PopperProps={{ popperOptions: { placement: 'right-end' } }}>
              <IconButton aria-label="edit" onClick={() => onEditUserClick(row)} data-cy="edit-row" size="large">
                <EditIcon />
              </IconButton>
            </Tooltip>
            {canDeleteUser(row) && (
              <Tooltip
                title={<span className={classes.tooltip}>Delete</span>}
                PopperProps={{ popperOptions: { placement: 'right-end' } }}
              >
                <IconButton aria-label="delete" onClick={() => onDeleteUserClick(row)} data-cy="delete-row" size="large">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const gridOptions: DataGridProProps = {
    rows: getUserRows(),
    'aria-label': 'Aptean admin users table',
    columns,
    sortingOrder: ['desc', 'asc'],
    sortModel,
    onSortModelChange: setSortModel,
  };

  return (
    <Paper className={classes.paper} role="region" aria-label="Manage Aptean Admin Users">
      <Helmet>
        <title>Aptean Pay Merchant Portal - Aptean Admin Users</title>
      </Helmet>
      <Dialog
        aria-label={'modify user dialog'}
        maxWidth={false}
        open={openModify}
        onClose={handleUserFormClose}
        disableScrollLock
        disableEscapeKeyDown={networkBusy}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <UserForm
          close={handleUserFormClose}
          user={user}
          submit={submitUpsertForm}
          userError={userError}
          networkBusy={networkBusy}
          viewerUser={viewerUser}
          hasOneAdmin={false}
          hideRole
        />
      </Dialog>
      <Dialog
        aria-label={'delete user dialog'}
        maxWidth={false}
        open={openDelete}
        onClose={handleUserDeleteFormClose}
        disableScrollLock
        disableEscapeKeyDown={networkBusy}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <UserDeleteForm
          close={handleUserDeleteFormClose}
          user={user}
          submit={submitDeleteForm}
          userError={userError}
          networkBusy={networkBusy}
        />
      </Dialog>
      <Toolbar className={classes.toolbar} variant="regular">
        <Box className={classes.title} data-cy="manage-aptean-admin-users-header">
          <Typography variant="title">Manage Aptean Admin Users</Typography>
        </Box>
        <Button
          color="primary"
          variant="outlined"
          aria-label="add new user"
          className={classes.toolbarButton}
          onClick={onAddUserClick}
          data-cy="add-new-aptean-admin-user"
        >
          + NEW USER
        </Button>
      </Toolbar>
      <Box className={classes.usersTable} data-cy="aptean-admin-users-table" role="region" aria-label="All Aptean Admin Users">
        <DataList
          gridOptions={gridOptions}
          isSkeletonHidden={true}
          cypressTag="aptean-admin-users-table-body"
          noDataText="No users to show."
          sortingMode="client"
        />
      </Box>
    </Paper>
  );
};

export default ApteanAdminUsers;
